import { useCallback, useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import U2000SaveCancelDialog from '../../../../u2000Core/components/dialog/U2000SaveCancelDialog';
import U2000TagAutocomplete from '../../../../u2000Core/components/tables/commons/search/U2000TagAutocomplete';
import type { U2000CriteriaTag } from '../../../../u2000Core/components/tables/commons/search/u2000CriteriaTags';
import { NS } from '../../../U3206';
import {
  u3206AssignedGroupLabel,
  u3206FilterUsersForAutocomplete,
  u3206FormatUserLabel,
  type U3206GroupMemberLink,
  type U3206UserRef,
} from '../../../U3206Service';

interface U3206UsersAddDlgProps {
  isOpen: boolean;
  onClose: (action: 'save' | 'cancel') => void;
  onConfirm: (links: U3206GroupMemberLink[]) => void;
  catalog: U3206UserRef[];
  linkedUserIds: string[];
  currentGroupId?: string | null;
  t: TFunction;
}

function U3206UsersAddDlg({
  isOpen,
  onClose,
  onConfirm,
  catalog,
  linkedUserIds,
  currentGroupId,
  t,
}: U3206UsersAddDlgProps) {
  const [tags, setTags] = useState<U2000CriteriaTag[]>([]);

  /** Membres déjà sur le groupe + déjà choisis dans ce dialog. */
  const excludedUserIds = useMemo(
    () => [...linkedUserIds, ...tags.map((tag) => tag.value).filter(Boolean)],
    [linkedUserIds, tags],
  );

  const handleClose = useCallback(
    (action: 'save' | 'cancel') => {
      if (action === 'save') {
        const links = tags
          .map((tag) => {
            const meta = tag.meta as U3206UserRef | undefined;
            if (!meta?.id) return null;
            return {
              userId: meta.id,
              username: meta.username,
              firstName: meta.firstName,
              lastName: meta.lastName,
            } satisfies U3206GroupMemberLink;
          })
          .filter((link) => link != null) as U3206GroupMemberLink[];
        onConfirm(links);
      }
      setTags([]);
      onClose(action);
    },
    [onClose, onConfirm, tags],
  );

  if (!isOpen) return null;

  return (
    <U2000SaveCancelDialog
      isOpen={isOpen}
      onClose={handleClose}
      title={t('dlgAddUsersTitle', { ns: NS })}
      width="large"
      isValid={tags.length > 0}
      contentOverflowVisible
    >
      <div className="flex flex-col gap-y-2">
        <label className="text-sm font-medium text-gray-700">{t('lblSelectUsers', { ns: NS })}</label>
        <U2000TagAutocomplete<U3206UserRef>
          value={tags}
          onChange={setTags}
          allowFreeText={false}
          minSearchLength={0}
          searchCriterionField="username"
          placeholder={t('placeholderUserTags', { ns: NS })}
          searchingLabel={t('lblSearching', { ns: NS })}
          noResultsLabel={(query) => t('lblNoUserResults', { ns: NS, query })}
          onSearch={async (criterias) => {
            const query = String(criterias.find((c) => c.fieldName === 'username')?.value ?? '');
            return u3206FilterUsersForAutocomplete(catalog, excludedUserIds, query, currentGroupId);
          }}
          itemToTag={(user) => ({
            value: user.id,
            label: u3206FormatUserLabel(user),
            meta: user,
          })}
          getTagKey={(tag) => tag.value}
          getTagLabel={(tag) => tag.label ?? tag.value}
          renderSuggestion={(user) => {
            const groupLabel = u3206AssignedGroupLabel(user);
            return (
              <>
                <span className="font-semibold">{u3206FormatUserLabel(user)}</span>
                {groupLabel ? (
                  <span className="ml-2 text-gray-500">
                    {t('lblUserAssignedGroup', { ns: NS, group: groupLabel })}
                  </span>
                ) : null}
              </>
            );
          }}
          removeTagAriaLabel={(tag) => t('ariaRemoveTag', { ns: NS, label: tag.label ?? tag.value })}
          footerText={t('lblUserTagCount', { ns: NS, count: tags.length })}
        />
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U3206UsersAddDlg;
